import React, { useState, useEffect, useCallback } from 'react';
import { FaSave, FaTimes, FaFileAlt, FaDownload } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { calculateCarpetEstimate } from '../utils/estimateUtils';

const SaveModal = ({ onClose, onSave }) => {
  const [fileName, setFileName] = useState('');
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Enter' && fileName.trim()) {
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fileName, onClose]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-save-modal]')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const captureCanvasAsImage = async () => {
    try {
      // Try to find the PIXI canvas
      const canvas = document.querySelector('canvas');
      if (canvas) {
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        
        // Fill with white background
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        
        // Draw the PIXI canvas
        tempCtx.drawImage(canvas, 0, 0);
        
        return tempCanvas.toDataURL('image/png', 1.0);
      }
      return null;
    } catch (error) {
      console.error('Error capturing canvas:', error);
      return null;
    }
  };

  // Generate cutting plan diagram for a room
  const generateCuttingPlanImage = (room, estimate) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = 400;
      canvas.height = 300;
      
      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw border
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
      
      // Title
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${room.name} - Cutting Plan`, canvas.width / 2, 35);
      
      // Room dimensions
      ctx.font = '12px Arial';
      ctx.fillText(`${room.width}m × ${room.height}m`, canvas.width / 2, 55);
      
      if (estimate.cuttingPlan && estimate.cuttingPlan.cuts) {
        const cuts = estimate.cuttingPlan.cuts;
        const startY = 80;
        const pieceHeight = 40;
        const margin = 20;
        
        cuts.forEach((cut, index) => {
          const y = startY + (index * pieceHeight);
          
          // Draw piece rectangle
          ctx.fillStyle = index % 2 === 0 ? '#e0e0e0' : '#f0f0f0';
          ctx.fillRect(margin, y, canvas.width - (2 * margin), pieceHeight - 10);
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 1;
          ctx.strokeRect(margin, y, canvas.width - (2 * margin), pieceHeight - 10);
          
          // Piece label
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 12px Arial';
          ctx.textAlign = 'left';
          ctx.fillText(cut.label, margin + 5, y + 15);
          
          // Dimensions
          ctx.font = '10px Arial';
          ctx.fillText(`${cut.width.toFixed(1)}m × ${cut.length.toFixed(1)}m`, margin + 5, y + 30);
          
          // Waste info
          if (cut.waste > 0) {
            ctx.fillStyle = '#ff0000';
            ctx.fillText(`Waste: ${cut.waste.toFixed(2)} m²`, canvas.width - margin - 80, y + 15);
          }
          
          // Seam indicator
          if (cut.seam) {
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(margin, y + (pieceHeight - 10) / 2);
            ctx.lineTo(canvas.width - margin, y + (pieceHeight - 10) / 2);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        });
        
        // Legend
        const legendY = startY + (cuts.length * pieceHeight) + 20;
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('Legend:', margin, legendY);
        
        ctx.font = '10px Arial';
        ctx.fillText('• Alternating colors = Different pieces', margin, legendY + 15);
        ctx.fillText('• Red dashed line = Seam location', margin, legendY + 30);
        ctx.fillText('• Red text = Waste area', margin, legendY + 45);
      }
      
      return canvas.toDataURL('image/png', 1.0);
    } catch (error) {
      console.error('Error generating cutting plan image:', error);
      return null;
    }
  };

  const generatePDF = async (planData) => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');
      
      // Capture canvas image
      const canvasImage = await captureCanvasAsImage();
      
      // Generate rooms data and estimates
      const rooms = generateRoomsData(planData);
      const { calculateCarpetEstimate } = await import('../utils/estimateUtils');
      
      // Ensure shapes is always an array and include shapes from merged structures
      const individualShapes = Array.isArray(planData.shapes) ? planData.shapes : [];
      const mergedStructures = Array.isArray(planData.mergedStructures) ? planData.mergedStructures : [];
      
      // Collect all shapes including those from merged structures
      const allShapes = [...individualShapes];
      mergedStructures.forEach(structure => {
        if (structure.shapes && Array.isArray(structure.shapes)) {
          allShapes.push(...structure.shapes);
        }
      });
      
      const { estimates, totals } = calculateCarpetEstimate(rooms, allShapes);

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;

      // Title
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Floor Plan & Carpet Estimate', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Date
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Floor Plan Image
      if (canvasImage) {
        try {
          const img = new Image();
          img.src = canvasImage;
          
          await new Promise((resolve) => {
            img.onload = () => {
              const imgAspectRatio = img.width / img.height;
              const maxImgWidth = contentWidth;
              const maxImgHeight = 120;
              
              let imgWidth = maxImgWidth;
              let imgHeight = imgWidth / imgAspectRatio;
              
              if (imgHeight > maxImgHeight) {
                imgHeight = maxImgHeight;
                imgWidth = imgHeight * imgAspectRatio;
              }
              
              const imgX = margin + (contentWidth - imgWidth) / 2;
              pdf.addImage(img, 'PNG', imgX, yPosition, imgWidth, imgHeight);
              yPosition += imgHeight + 15;
              resolve();
            };
          });
        } catch (error) {
          console.warn('Could not add floor plan image:', error);
        }
      }

      // Project Summary
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Project Summary', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Total Rooms: ${rooms.length}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Total Area: ${totals.area.toFixed(2)} m²`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Carpet Area Required: ${totals.carpetArea.toFixed(2)} m²`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Total Cost: $${totals.cost.toFixed(2)}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Total Seam Length: ${totals.seamLength.toFixed(1)} m`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Total Rolls Needed: ${totals.rollsTotal}`, margin, yPosition);
      yPosition += 15;

      // Check if we need a new page
      if (yPosition > pageHeight - 100) {
        pdf.addPage();
        yPosition = margin;
      }

      // Detailed Room Estimates
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Detailed Room Estimates', margin, yPosition);
      yPosition += 15;

      for (let i = 0; i < estimates.length; i++) {
        const room = estimates[i];
        const estimate = room.estimate;

        // Check if we need a new page
        if (yPosition > pageHeight - 150) {
          pdf.addPage();
          yPosition = margin;
        }

        // Room header
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${room.name}`, margin, yPosition);
        yPosition += 8;

        // Room details
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        pdf.text(`Area: ${room.area} m² | Dimensions: ${room.width}m × ${room.height}m`, margin, yPosition);
        yPosition += 6;
        pdf.text(`Shape Type: ${room.shapeType} | Complexity: ${room.carpetComplexity || 'standard'}`, margin, yPosition);
        yPosition += 6;

        // Carpet details
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Roll: ${estimate.rollName} (${estimate.rollWidth}m wide)`, margin, yPosition);
        yPosition += 6;
        pdf.text(`Rolls Needed: ${estimate.rollsNeeded} | Length: ${estimate.lengthNeeded.toFixed(1)}m`, margin, yPosition);
        yPosition += 6;
        pdf.text(`Orientation: ${estimate.orientation} | Pile Direction: ${estimate.pileDirection}`, margin, yPosition);
        yPosition += 6;

        // Cost breakdown
        pdf.setFont('helvetica', 'bold');
        pdf.text('Cost Breakdown:', margin, yPosition);
        yPosition += 6;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`• Carpet Material: $${estimate.costs.carpet.toFixed(2)}`, margin + 5, yPosition);
        yPosition += 5;
        pdf.text(`• Installation: $${estimate.costs.installation.toFixed(2)}`, margin + 5, yPosition);
        yPosition += 5;
        if (estimate.costs.seaming > 0) {
          pdf.text(`• Seaming: $${estimate.costs.seaming.toFixed(2)}`, margin + 5, yPosition);
          yPosition += 5;
        }
        if (estimate.costs.transitions > 0) {
          pdf.text(`• Transitions: $${estimate.costs.transitions.toFixed(2)}`, margin + 5, yPosition);
          yPosition += 5;
        }
        if (estimate.costs.complexity > 0) {
          pdf.text(`• Complexity Surcharge: $${estimate.costs.complexity.toFixed(2)}`, margin + 5, yPosition);
          yPosition += 5;
        }
        if (estimate.costs.shapeComplexity > 0) {
          pdf.text(`• Shape Complexity: $${estimate.costs.shapeComplexity.toFixed(2)}`, margin + 5, yPosition);
          yPosition += 5;
        }
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Total Room Cost: $${estimate.costs.total.toFixed(2)}`, margin, yPosition);
        yPosition += 8;

        // Installation factors
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        pdf.text(`Installation Waste: ${estimate.installationFactors.installationWaste}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Door Adjustments: ${estimate.installationFactors.doorAdjustments}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Complexity Factor: ${estimate.installationFactors.complexityFactor}`, margin, yPosition);
        yPosition += 5;
        if (estimate.installationFactors.doorCount > 0) {
          pdf.text(`Doors: ${estimate.installationFactors.doorCount} (${estimate.installationFactors.doorComplexity})`, margin, yPosition);
          yPosition += 5;
        }
        yPosition += 5;

        // Cutting plan
        if (estimate.cuttingPlan) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('Cutting Plan:', margin, yPosition);
          yPosition += 6;
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Strategy: ${estimate.cuttingPlan.cuttingStrategy}`, margin + 5, yPosition);
          yPosition += 5;
          pdf.text(`Estimated Time: ${estimate.cuttingPlan.estimatedCuttingTime}`, margin + 5, yPosition);
          yPosition += 5;
          pdf.text(`Total Waste: ${estimate.cuttingPlan.totalWaste.toFixed(2)} m²`, margin + 5, yPosition);
          yPosition += 5;

          // Generate and add cutting plan image
          try {
            const cuttingPlanImage = generateCuttingPlanImage(room, estimate);
            if (cuttingPlanImage) {
              const img = new Image();
              img.src = cuttingPlanImage;
              
              await new Promise((resolve) => {
                img.onload = () => {
                  const imgAspectRatio = img.width / img.height;
                  const maxImgWidth = contentWidth;
                  const maxImgHeight = 80;
                  
                  let imgWidth = maxImgWidth;
                  let imgHeight = imgWidth / imgAspectRatio;
                  
                  if (imgHeight > maxImgHeight) {
                    imgHeight = maxImgHeight;
                    imgWidth = imgHeight * imgAspectRatio;
                  }
                  
                  const imgX = margin + (contentWidth - imgWidth) / 2;
                  pdf.addImage(img, 'PNG', imgX, yPosition, imgWidth, imgHeight);
                  yPosition += imgHeight + 10;
                  resolve();
                };
              });
            }
          } catch (error) {
            console.warn('Could not add cutting plan image:', error);
          }

          // Special notes
          if (estimate.cuttingPlan.specialNotes && estimate.cuttingPlan.specialNotes.length > 0) {
            pdf.text('Special Notes:', margin + 5, yPosition);
            yPosition += 5;
            estimate.cuttingPlan.specialNotes.forEach(note => {
              pdf.text(`• ${note}`, margin + 10, yPosition);
              yPosition += 4;
            });
            yPosition += 2;
          }

          // Individual cuts
          if (estimate.cuttingPlan.cuts && estimate.cuttingPlan.cuts.length > 0) {
            pdf.text('Cut Pieces:', margin + 5, yPosition);
            yPosition += 5;
            estimate.cuttingPlan.cuts.forEach(cut => {
              pdf.text(`• ${cut.label}: ${cut.width.toFixed(1)}m × ${cut.length.toFixed(1)}m`, margin + 10, yPosition);
              yPosition += 4;
              if (cut.waste > 0) {
                pdf.text(`  Waste: ${cut.waste.toFixed(2)} m²`, margin + 15, yPosition);
                yPosition += 4;
              }
            });
            yPosition += 2;
          }
        }

        // Special instructions
        if (estimate.specialInstructions && estimate.specialInstructions.length > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          pdf.text('Installation Instructions:', margin, yPosition);
          yPosition += 6;
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(60, 60, 60);
          estimate.specialInstructions.forEach(instruction => {
            // Check if instruction fits on current page
            if (yPosition > pageHeight - 50) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(instruction, margin + 5, yPosition);
            yPosition += 4;
          });
          yPosition += 5;
        }

        yPosition += 10; // Space between rooms
      }

      // Check if we need a new page for totals
      if (yPosition > pageHeight - 100) {
        pdf.addPage();
        yPosition = margin;
      }

      // Final totals
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Project Totals', margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Total Carpet Area: ${totals.carpetArea.toFixed(2)} m²`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Total Material Cost: $${totals.cost.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Total Seam Length: ${totals.seamLength.toFixed(1)} m`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Total Rolls Required: ${totals.rollsTotal}`, margin, yPosition);
      yPosition += 15;

      // Footer
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Generated by React PIXI Floor Planner', pageWidth / 2, pageHeight - 10, { align: 'center' });

      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      return null;
    }
  };

  // Generate rooms data using the same logic as FloorPlanner
  const generateRoomsData = (planData) => {
    try {
      const shapes = Array.isArray(planData.shapes) ? planData.shapes : [];
      const mergedStructures = Array.isArray(planData.mergedStructures) ? planData.mergedStructures : [];

      // Helper functions (same as FloorPlanner)
      const calculateShapeArea = (shape) => {
        switch (shape.type) {
          case "rectangle":
            if (shape.lineData && shape.lineData.some((line) => line && line.isCurveMode)) {
              const baseArea = (shape.width * shape.height) / 10000;
              const curveCount = shape.lineData.filter((line) => line && line.isCurveMode).length;
              const curveAdjustment = curveCount * 0.1;
              return baseArea * (1 + curveAdjustment);
            }
            return (shape.width * shape.height) / 10000;
          case "circle":
            return Math.PI * Math.pow(shape.radius / 100, 2);
          case "triangle":
            return (shape.width * shape.height) / 2 / 10000;
          case "door":
            const radius = shape.radius || 50;
            return (Math.PI * Math.pow(radius / 100, 2)) / 2;
          default:
            return 0;
        }
      };

      const getEquivalentDimensions = (shape) => {
        switch (shape.type) {
          case "rectangle":
            return { width: shape.width / 100, height: shape.height / 100 };
          case "circle":
            const diameter = (shape.radius * 2) / 100;
            return { width: diameter, height: diameter };
          case "triangle":
            return { width: shape.width / 100, height: shape.height / 100 };
          case "door":
            const doorDiameter = ((shape.radius || 50) * 2) / 100;
            return { width: doorDiameter, height: doorDiameter };
          default:
            return { width: 1, height: 1 };
        }
      };

      // Get available shapes (exclude merged ones)
      const getAvailableShapes = () => {
        const mergedShapeIds = new Set();
        mergedStructures.forEach((structure) => {
          if (structure.shapes && Array.isArray(structure.shapes)) {
            structure.shapes.forEach((shape) => {
              mergedShapeIds.add(shape.id);
            });
          }
        });
        return shapes.filter((shape) => !mergedShapeIds.has(shape.id));
      };

      // Create individual rooms
      const availableShapes = getAvailableShapes();
      const individualRooms = availableShapes
        .filter((shape) => ["rectangle", "circle", "triangle", "door"].includes(shape.type))
        .map((shape, index) => {
          const area = calculateShapeArea(shape);
          const dimensions = getEquivalentDimensions(shape);
          const hasCurves = shape.lineData && shape.lineData.some((line) => line && line.isCurveMode);
          const carpetComplexity = shape.type === "rectangle" && !hasCurves ? "standard" : "complex";

          // Use the shape's name if available, otherwise generate a default name
          const roomName = shape.name || `${shape.type.charAt(0).toUpperCase() + shape.type.slice(1)} Room ${index + 1}`;

          return {
            id: `individual-${shape.id}`,
            name: roomName,
            area: area.toFixed(2),
            width: dimensions.width.toFixed(1),
            height: dimensions.height.toFixed(1),
            type: "individual",
            shapeType: shape.type,
            carpetComplexity,
            originalShape: shape,
          };
        });

      // Create merged rooms
      const mergedRooms = mergedStructures.map((structure, index) => {
        const structureShapes = Array.isArray(structure.shapes) ? structure.shapes : [];
        const totalArea = structureShapes.reduce((sum, shape) => sum + calculateShapeArea(shape), 0);
        
        // Calculate bounds
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        structureShapes.forEach((shape) => {
          const left = shape.x;
          const top = shape.y;
          const right = shape.x + (shape.width || shape.radius * 2 || 0);
          const bottom = shape.y + (shape.height || shape.radius * 2 || 0);

          minX = Math.min(minX, left);
          minY = Math.min(minY, top);
          maxX = Math.max(maxX, right);
          maxY = Math.max(maxY, bottom);
        });

        const hasNonRectangular = structureShapes.some((s) => s.type !== "rectangle");
        const hasCurvedRectangles = structureShapes.some(
          (s) =>
            s.type === "rectangle" &&
            s.lineData &&
            s.lineData.some((line) => line && line.isCurveMode)
        );

        // Try to get a meaningful name from the merged structure
        let roomName = `Complex Room ${index + 1}`;
        if (structure.name) {
          roomName = structure.name;
        } else if (structureShapes.length > 0) {
          // Use the name of the first rectangle in the structure
          const firstRectangle = structureShapes.find(s => s.type === 'rectangle');
          if (firstRectangle && firstRectangle.name) {
            roomName = firstRectangle.name;
          }
        }

        return {
          id: `merged-${index}`,
          name: roomName,
          area: totalArea.toFixed(2),
          width: ((maxX - minX) / 100).toFixed(1),
          height: ((maxY - minY) / 100).toFixed(1),
          type: "merged",
          complexity: structureShapes.length,
          shapes: structureShapes,
          hasNonRectangular,
          hasCurvedRectangles,
        };
      });

      return [...individualRooms, ...mergedRooms];
    } catch (error) {
      console.error('Error generating rooms data:', error);
      return [];
    }
  };

  const handleSave = async () => {
    if (!fileName.trim()) {
      setError('Please enter a file name');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Get current plan data from localStorage
      const planData = {
        shapes: JSON.parse(localStorage.getItem('react-pixi-floor-planner-state') || '[]'),
        mergedStructures: JSON.parse(localStorage.getItem('react-pixi-floor-planner-merged-structures') || '[]'),
        fileName: fileName.trim(),
        savedAt: new Date().toISOString(),
        saveForFuture: saveForFuture
      };

      if (saveForFuture) {
        // Save as JSON file
        const jsonBlob = new Blob([JSON.stringify(planData, null, 2)], { type: 'application/json' });
        const jsonUrl = URL.createObjectURL(jsonBlob);
        const jsonLink = document.createElement('a');
        jsonLink.href = jsonUrl;
        jsonLink.download = `${fileName.trim()}.json`;
        jsonLink.click();
        URL.revokeObjectURL(jsonUrl);

        // Also save to localStorage for future use
        const savedPlans = JSON.parse(localStorage.getItem('saved-floor-plans') || '[]');
        savedPlans.push({
          fileName: fileName.trim(),
          savedAt: new Date().toISOString(),
          data: planData
        });
        localStorage.setItem('saved-floor-plans', JSON.stringify(savedPlans));
      } else {
        // Only generate PDF when save-for-future is not selected
        const pdf = await generatePDF(planData);
        if (pdf) {
          pdf.save(`${fileName.trim()}.pdf`);
        }
      }

      onSave && onSave(fileName.trim(), saveForFuture);
      onClose();
    } catch (error) {
      console.error('Error saving plan:', error);
      setError('Error saving plan. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '16px',
    minWidth: '400px',
    maxWidth: '500px',
    animation: 'slideInFromTop 0.3s ease-out'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    fontSize: '24px',
    fontWeight: 'bold'
  };

  const inputGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box'
  };

  const checkboxStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    cursor: 'pointer'
  };

  const checkboxInputStyle = {
    width: '20px',
    height: '20px',
    accentColor: '#1a1a1a'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const saveButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    backdropFilter: 'blur(10px)'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  const errorStyle = {
    color: '#ff6b6b',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center'
  };

  return (
    <>
      <style>
        {`
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translate(-50%, -60%);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }
        `}
      </style>
      <div style={modalStyle} data-save-modal>
        <div style={headerStyle}>
          <FaSave />
          Save Floor Plan
        </div>
        
        {error && <div style={errorStyle}>{error}</div>}
        
        <div style={inputGroupStyle}>
          <label style={labelStyle}>File Name:</label>
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Enter plan name..."
            style={inputStyle}
            autoFocus
          />
        </div>

        <div style={checkboxStyle} onClick={() => setSaveForFuture(!saveForFuture)}>
          <input
            type="checkbox"
            checked={saveForFuture}
            onChange={(e) => setSaveForFuture(e.target.checked)}
            style={checkboxInputStyle}
          />
          <span>Save for future use</span>
        </div>

        {saveForFuture && (
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.1)', 
            padding: '16px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FaFileAlt />
              <strong>Will save:</strong>
            </div>
            <ul style={{ margin: '0', paddingLeft: '20px' }}>
              <li>Complete plan data (.json)</li>
              <li>Plan visualization (.pdf)</li>
              <li>Available for future loading</li>
            </ul>
          </div>
        )}

        <div style={buttonContainerStyle}>
          <button 
            style={cancelButtonStyle}
            onClick={onClose}
            disabled={isSaving}
          >
            <FaTimes />
            Cancel
          </button>
          <button 
            style={saveButtonStyle}
            onClick={handleSave}
            disabled={isSaving || !fileName.trim()}
          >
            {isSaving ? (
              <>
                <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                Saving...
              </>
            ) : (
              <>
                <FaDownload />
                Save Plan
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default SaveModal; 